import { useEffect, useRef, useState } from "react";
import { Box, IconButton, Skeleton, Stack, TextField, Typography } from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import DoneAllRoundedIcon from "@mui/icons-material/DoneAllRounded";
import DoneRoundedIcon from "@mui/icons-material/DoneRounded";

// Default/brand green — used where there's no specific conversation to key a color off
// (e.g. the empty state).
const WA_GREEN = "#25D366";

// You can have multiple WhatsApp business numbers, same as multiple Instagram accounts —
// so each thread is colored by which of our numbers it's on. Numbers aren't known ahead
// of time (unlike IG's hardcoded ACCOUNT_COLORS map), so the color is derived deterministically
// from the number itself instead.
const WA_PALETTE = ["#25D366", "#128C7E", "#075E54", "#34B7F1", "#00A884", "#008069", "#0F969C"];

function getNumberColor(key) {
  const str = String(key || "");
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = (hash * 31 + str.charCodeAt(i)) >>> 0;
  return WA_PALETTE[hash % WA_PALETTE.length];
}

const FADE_SLIDE = {
  "@keyframes fadeSlideIn": {
    "0%": { opacity: 0, transform: "translateY(8px)" },
    "100%": { opacity: 1, transform: "translateY(0)" },
  },
};

const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;

function istDateKey(d) {
  const ist = new Date(d.getTime() + IST_OFFSET_MS);
  return `${ist.getUTCFullYear()}-${ist.getUTCMonth()}-${ist.getUTCDate()}`;
}

function formatTime(dateStr) {
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true, timeZone: "Asia/Kolkata" });
}

function formatDateLabel(dateStr) {
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "";
  const now = new Date();
  if (istDateKey(d) === istDateKey(now)) return "Today";
  if (istDateKey(d) === istDateKey(new Date(now.getTime() - 86400000))) return "Yesterday";
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric", timeZone: "Asia/Kolkata" });
}

function formatRelativeTime(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "";
  const diffMs = Date.now() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHrs = Math.floor(diffMins / 60);
  if (diffHrs < 24) return `${diffHrs}h ago`;
  const diffDays = Math.floor(diffHrs / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", timeZone: "Asia/Kolkata" });
}

function groupMessagesByDate(messages) {
  const groups = {};
  for (const msg of messages) {
    const label = formatDateLabel(msg.sentAt);
    if (!groups[label]) groups[label] = [];
    groups[label].push(msg);
  }
  return groups;
}

function DateSeparator({ label }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", my: 2, gap: 1.5 }}>
      <Box sx={{ flex: 1, height: "1px", bgcolor: "rgba(0,0,0,0.06)" }} />
      <Typography
        sx={{
          fontSize: 10,
          fontWeight: 700,
          fontFamily: "Quicksand",
          color: "#9CA3AF",
          px: 1.5,
          py: 0.4,
          bgcolor: "rgba(0,0,0,0.04)",
          borderRadius: 99,
          letterSpacing: 0.5,
        }}
      >
        {label}
      </Typography>
      <Box sx={{ flex: 1, height: "1px", bgcolor: "rgba(0,0,0,0.06)" }} />
    </Box>
  );
}

// Delivery tick — WhatsApp-native visual language (single/double/blue check) rather than
// reusing IG's plain bubble, since status is a first-class part of the WhatsApp UX.
function StatusTick({ status }) {
  if (!status) return null;
  if (status === "read") return <DoneAllRoundedIcon sx={{ fontSize: 13, color: "#53BDEB" }} />;
  if (status === "delivered") return <DoneAllRoundedIcon sx={{ fontSize: 13, color: "rgba(255,255,255,0.65)" }} />;
  if (status === "sent") return <DoneRoundedIcon sx={{ fontSize: 13, color: "rgba(255,255,255,0.65)" }} />;
  return null;
}

function MessageBubble({ message, accentColor = WA_GREEN }) {
  const isUs = message.isFromUs;
  const isSending = message._status === "sending";
  const isFailed = message._status === "failed";
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: isUs ? "flex-end" : "flex-start",
        mb: 0.75,
        opacity: isSending ? 0.6 : 1,
        ...FADE_SLIDE,
        animation: "fadeSlideIn 0.2s ease both",
      }}
    >
      <Box
        sx={{
          maxWidth: { xs: "85%", sm: "75%", md: "68%" },
          background: isFailed
            ? "rgba(229,57,53,0.12)"
            : isUs
            ? `linear-gradient(135deg, ${accentColor} 0%, ${accentColor}CC 100%)`
            : "rgba(255,255,255,0.97)",
          color: isFailed ? "#E53935" : isUs ? "#fff" : "#1C1C2E",
          borderRadius: isUs ? "18px 18px 4px 18px" : "18px 18px 18px 4px",
          px: 2,
          py: 1.25,
          boxShadow: isUs ? `0 4px 16px ${accentColor}40` : "0 2px 8px rgba(0,0,0,0.07)",
          border: isFailed ? "1px solid rgba(229,57,53,0.3)" : isUs ? "none" : "1px solid rgba(0,0,0,0.06)",
        }}
      >
        {message.text ? (
          <Typography sx={{ fontSize: 13.5, fontFamily: "Quicksand", fontWeight: 600, lineHeight: 1.55, wordBreak: "break-word" }}>
            {message.text}
          </Typography>
        ) : (
          <Typography sx={{ fontSize: 12, fontFamily: "Quicksand", fontStyle: "italic", opacity: 0.55 }}>
            [{message.type || "attachment"}]
          </Typography>
        )}
        <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={0.4} sx={{ mt: 0.5 }}>
          <Typography sx={{ fontSize: 10, fontFamily: "Quicksand", color: isUs ? "rgba(255,255,255,0.65)" : "#9CA3AF" }}>
            {formatTime(message.sentAt)}
          </Typography>
          {isUs && !isFailed && !isSending && <StatusTick status={message.status} />}
        </Stack>
      </Box>
    </Box>
  );
}

function Composer({ conversation, pending, onSend, onRetry, onDismiss }) {
  const [text, setText] = useState("");
  const accentColor = getNumberColor(conversation?.ourPhoneNumberId || conversation?.ourDisplayPhoneNumber);
  const noToken = !conversation?.hasActiveToken;
  const windowClosed = conversation?.messagingWindow === "closed";
  const disabled = noToken || windowClosed;

  const handleSend = () => {
    const trimmed = text.trim();
    if (!trimmed || disabled) return;
    setText("");
    onSend(trimmed);
  };

  return (
    <Box sx={{ borderTop: "1px solid rgba(0,0,0,0.06)", background: "rgba(255,255,255,0.85)", flexShrink: 0 }}>
      {pending?.filter((p) => p.status === "failed").map((p) => (
        <Box key={p.tempId} sx={{ px: 2.5, pt: 1, display: "flex", alignItems: "center", gap: 1 }}>
          <Typography sx={{ fontSize: 11, fontFamily: "Quicksand", color: "#E53935" }}>
            Failed to send "{p.text.slice(0, 30)}{p.text.length > 30 ? "…" : ""}"
          </Typography>
          <Typography onClick={() => onRetry(p)} sx={{ fontSize: 11, fontFamily: "Quicksand", fontWeight: 800, color: accentColor, cursor: "pointer" }}>
            Retry
          </Typography>
          <Typography onClick={() => onDismiss(p.tempId)} sx={{ fontSize: 11, fontFamily: "Quicksand", fontWeight: 700, color: "#9CA3AF", cursor: "pointer" }}>
            Dismiss
          </Typography>
        </Box>
      ))}
      {disabled && (
        <Typography sx={{ px: 2.5, py: 1.5, fontSize: 12, fontFamily: "Quicksand", color: "#9CA3AF", fontStyle: "italic" }}>
          {noToken
            ? "Sending is unavailable — no active WhatsApp number connected."
            : "This creator hasn't messaged in the last 24 hours — WhatsApp only allows a pre-approved template message until they message again."}
        </Typography>
      )}
      {!disabled && (
        <Stack direction="row" alignItems="flex-end" spacing={1} sx={{ px: 2, py: 1.5 }}>
          <TextField
            fullWidth
            multiline
            maxRows={4}
            size="small"
            placeholder="Type a message…"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "18px",
                fontFamily: "Quicksand",
                fontSize: 13.5,
                background: "rgba(0,0,0,0.02)",
              },
            }}
          />
          <IconButton
            onClick={handleSend}
            disabled={!text.trim()}
            sx={{
              bgcolor: text.trim() ? accentColor : "rgba(0,0,0,0.06)",
              color: text.trim() ? "#fff" : "#9CA3AF",
              "&:hover": { bgcolor: text.trim() ? accentColor : "rgba(0,0,0,0.06)", opacity: 0.9 },
              width: 38,
              height: 38,
            }}
          >
            <SendRoundedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Stack>
      )}
    </Box>
  );
}

function ChatPanel({ conversation, isLoading, pending, onSend, onRetry, onDismiss }) {
  const scrollRef = useRef(null);
  const accentColor = getNumberColor(conversation?.ourPhoneNumberId || conversation?.ourDisplayPhoneNumber);
  const messages = conversation?.messages || [];
  const pendingForThisConv = (pending || []).filter((p) => p.conversationId === conversation?.conversationId);
  const combined = [
    ...messages,
    ...pendingForThisConv.map((p) => ({
      waMessageId: p.tempId,
      text: p.text,
      sentAt: p.sentAt,
      isFromUs: true,
      _status: p.status,
    })),
  ];
  const grouped = groupMessagesByDate(combined);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [conversation, pending]);

  if (isLoading) {
    return (
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1.5, p: 2.5 }}>
        {[...Array(6)].map((_, i) => (
          <Box key={i} sx={{ display: "flex", justifyContent: i % 2 === 0 ? "flex-end" : "flex-start" }}>
            <Skeleton variant="rounded" width={`${40 + (i % 3) * 15}%`} height={44} sx={{ borderRadius: i % 2 === 0 ? "18px 18px 4px 18px" : "18px 18px 18px 4px" }} />
          </Box>
        ))}
      </Box>
    );
  }

  return (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <Box
        sx={{
          px: { xs: 1.5, sm: 2.5 },
          py: 1.5,
          borderBottom: "1px solid rgba(0,0,0,0.06)",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          background: "rgba(255,255,255,0.8)",
          backdropFilter: "blur(8px)",
          flexShrink: 0,
        }}
      >
        <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: accentColor, boxShadow: `0 0 0 3px ${accentColor}30` }} />
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontFamily: "Quicksand", fontWeight: 800, fontSize: 14, color: "#1C1C2E", lineHeight: 1.3 }}>
            {conversation?.customerName || conversation?.customerWaId || "WhatsApp"}
          </Typography>
          {conversation?.ourDisplayPhoneNumber && (
            <Typography sx={{ fontFamily: "Quicksand", fontSize: 10.5, color: accentColor, fontWeight: 700, lineHeight: 1.2 }}>
              via {conversation.ourDisplayPhoneNumber}
            </Typography>
          )}
        </Box>
        <Typography sx={{ fontFamily: "Quicksand", fontSize: 12, color: "#9CA3AF", ml: "auto" }}>
          {messages.length} {messages.length === 1 ? "message" : "messages"}
        </Typography>
      </Box>

      <Box
        ref={scrollRef}
        sx={{
          flex: 1,
          overflowY: "auto",
          px: 2.5,
          py: 2,
          background: "linear-gradient(145deg, rgba(224,247,232,0.4) 0%, rgba(237,241,255,0.3) 100%)",
          "&::-webkit-scrollbar": { width: 4 },
          "&::-webkit-scrollbar-thumb": { bgcolor: "rgba(0,0,0,0.12)", borderRadius: 2 },
        }}
      >
        {Object.entries(grouped).map(([dateLabel, msgs]) => (
          <Box key={dateLabel}>
            <DateSeparator label={dateLabel} />
            {msgs.map((msg, i) => (
              <MessageBubble key={msg.waMessageId || `msg-${dateLabel}-${i}`} message={msg} accentColor={accentColor} />
            ))}
          </Box>
        ))}
      </Box>

      <Composer
        conversation={conversation}
        pending={pendingForThisConv}
        onSend={(text) => onSend(conversation.conversationId, text)}
        onRetry={onRetry}
        onDismiss={onDismiss}
      />
    </Box>
  );
}

function ConversationSidebar({ conversations, selectedIdx, onSelect }) {
  return (
    <Box
      sx={{
        width: { xs: "100%", sm: 180, md: 220 },
        flexShrink: 0,
        borderRight: "1px solid rgba(0,0,0,0.06)",
        overflowY: "auto",
        background: "rgba(255,255,255,0.6)",
        "&::-webkit-scrollbar": { width: 3 },
      }}
    >
      <Typography
        sx={{ px: 2, pt: 2, pb: 1, fontFamily: "Quicksand", fontSize: 10, fontWeight: 800, color: "#9CA3AF", letterSpacing: 1, textTransform: "uppercase" }}
      >
        Numbers
      </Typography>
      {conversations.map((conv, idx) => {
        const isSelected = idx === selectedIdx;
        // Labeled by our number, not the customer — the customer is the same creator on
        // every row here, what differs between rows is which of our numbers messaged them.
        const color = getNumberColor(conv.ourPhoneNumberId || conv.ourDisplayPhoneNumber);
        return (
          <Box
            key={conv.conversationId || idx}
            onClick={() => onSelect(idx)}
            sx={{
              px: 2,
              py: 1.5,
              cursor: "pointer",
              borderLeft: isSelected ? `3px solid ${color}` : "3px solid transparent",
              bgcolor: isSelected ? `${color}0D` : "transparent",
              transition: "all 0.15s ease",
              "&:hover": { bgcolor: `${color}0D` },
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: color, flexShrink: 0 }} />
              <Typography
                sx={{
                  fontFamily: "Quicksand",
                  fontWeight: isSelected ? 800 : 600,
                  fontSize: 12.5,
                  color: isSelected ? color : "#374151",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {conv.ourDisplayPhoneNumber || conv.conversationId}
              </Typography>
            </Stack>
            <Typography sx={{ fontFamily: "Quicksand", fontSize: 10, color: "#9CA3AF", pl: 2.25, mt: 0.25 }}>
              {formatRelativeTime(conv.lastMessageAt)}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

function EmptyState() {
  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 1.5,
        py: 8,
        ...FADE_SLIDE,
        animation: "fadeSlideIn 0.5s ease both",
      }}
    >
      <Box
        sx={{
          width: 72,
          height: 72,
          borderRadius: "50%",
          background: `linear-gradient(135deg, ${WA_GREEN}14 0%, ${WA_GREEN}0A 100%)`,
          border: `2px dashed ${WA_GREEN}38`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 0.5,
        }}
      >
        <WhatsAppIcon sx={{ fontSize: 28, color: WA_GREEN, opacity: 0.7 }} />
      </Box>
      <Typography sx={{ fontFamily: "Quicksand", fontWeight: 800, color: "#1C1C2E", fontSize: 15 }}>
        No WhatsApp conversations yet
      </Typography>
      <Typography sx={{ fontFamily: "Quicksand", color: "#6B7280", fontSize: 13, maxWidth: 300, textAlign: "center" }}>
        Once this creator messages our WhatsApp number, the conversation will appear here.
      </Typography>
    </Box>
  );
}

let pendingIdCounter = 0;

function CreatorWhatsAppTab({ conversations, isLoading, onSendMessage }) {
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [pending, setPending] = useState([]);
  const hasMultiple = conversations && conversations.length > 1;
  const selected = conversations?.[selectedIdx] || null;

  const handleSend = async (conversationId, text) => {
    const tempId = `pending-${++pendingIdCounter}`;
    setPending((prev) => [...prev, { tempId, conversationId, text, sentAt: new Date().toISOString(), status: "sending" }]);
    try {
      await onSendMessage(conversationId, text);
      setPending((prev) => prev.filter((p) => p.tempId !== tempId));
    } catch (err) {
      setPending((prev) => prev.map((p) => (p.tempId === tempId ? { ...p, status: "failed" } : p)));
    }
  };

  const handleRetry = (item) => {
    setPending((prev) => prev.filter((p) => p.tempId !== item.tempId));
    handleSend(item.conversationId, item.text);
  };

  const handleDismiss = (tempId) => {
    setPending((prev) => prev.filter((p) => p.tempId !== tempId));
  };

  if (!isLoading && (!conversations || conversations.length === 0)) {
    return <EmptyState />;
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        border: "1px solid rgba(0,0,0,0.07)",
        borderRadius: 3,
        overflow: "hidden",
        height: { xs: "auto", md: 520 },
        minHeight: { xs: 500, md: 520 },
        boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
        ...FADE_SLIDE,
        animation: "fadeSlideIn 0.35s ease both",
      }}
    >
      {hasMultiple && (
        <ConversationSidebar conversations={conversations} selectedIdx={selectedIdx} onSelect={setSelectedIdx} />
      )}
      <ChatPanel
        conversation={selected}
        isLoading={isLoading}
        pending={pending}
        onSend={handleSend}
        onRetry={handleRetry}
        onDismiss={handleDismiss}
      />
    </Box>
  );
}

export default CreatorWhatsAppTab;
